import React from "react";
import { Checkbox } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import { formatUtcEventDate } from "./promotionEventDateTime";
import {
  HEADER_STATES,
  isPromotionRowSelectable,
} from "./promotionSelection";
import {
  PROMOTIONS_LIST_MODES,
  SELECT_CHECKBOX_SX,
  STATUS_PILL_CLASS,
} from "./promotionListModes";

const formatDisplayDate = (iso) => formatUtcEventDate(iso, { shortYear: true });

export const PromotionsEligibilityTable = ({
  mode = PROMOTIONS_LIST_MODES.REPORT,
  promotions = [],
  selectionModel = null,
  selectedIdSet = null,
  onSelectAllMatching,
  onToggleOne,
  emptyMessage = "No eligible employees for this book",
}) => {
  const selectable = mode === PROMOTIONS_LIST_MODES.CREATE;
  const colSpan = selectable ? 4 : 7;

  return (
    <table className="promotions-table">
      <thead>
        <tr>
          {selectable ? (
            <th>
              <Checkbox
                size="small"
                disabled={
                  !selectionModel ||
                  selectionModel.headerState === HEADER_STATES.DISABLED
                }
                checked={
                  selectionModel?.headerState === HEADER_STATES.MINUS
                }
                indeterminate={
                  selectionModel?.headerState === HEADER_STATES.PLUS
                }
                indeterminateIcon={<AddBoxOutlinedIcon fontSize="small" />}
                checkedIcon={
                  <IndeterminateCheckBoxOutlinedIcon fontSize="small" />
                }
                onChange={onSelectAllMatching}
                inputProps={{
                  "aria-label":
                    selectionModel?.headerState === HEADER_STATES.MINUS
                      ? "Deselect all employees with this status"
                      : "Select all employees with this status",
                }}
                sx={SELECT_CHECKBOX_SX}
              />
              Select Employee
            </th>
          ) : (
            <th>Employee</th>
          )}
          <th>Company</th>
          <th>Completed On</th>
          {mode === PROMOTIONS_LIST_MODES.REPORT ? (
            <>
              <th>Status</th>
              <th>Contacted On</th>
              <th>Responded On</th>
              <th>Promotion Completed</th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {promotions.length === 0 ? (
          <tr>
            <td colSpan={colSpan} className="promotions-table__empty">
              {emptyMessage}
            </td>
          </tr>
        ) : (
          promotions.map((row) => {
            if (!selectable) {
              return (
                <tr key={row._id}>
                  <td>{row.employee?.name || "—"}</td>
                  <td>{row.company?.name || "—"}</td>
                  <td>{formatDisplayDate(row.completedOn)}</td>
                  <td>
                    <span
                      className={`promo-pill ${
                        STATUS_PILL_CLASS[row.status] || "promo-pill--pending"
                      }`}
                    >
                      {row.statusLabel || row.status}
                    </span>
                  </td>
                  <td>{formatDisplayDate(row.contactedOn)}</td>
                  <td>{formatDisplayDate(row.respondedOn)}</td>
                  <td>{formatDisplayDate(row.promotionCompletedOn)}</td>
                </tr>
              );
            }

            const isSelectable = isPromotionRowSelectable(
              row.status || "CONTACT_PENDING",
              selectionModel
            );
            const isChecked = selectedIdSet?.has(row._id);
            return (
              <tr
                key={row._id}
                className={
                  isSelectable ? undefined : "promotions-table__row--disabled"
                }
              >
                <td>
                  <label className="promotions-table__employee">
                    <Checkbox
                      size="small"
                      checked={Boolean(isChecked)}
                      disabled={!isSelectable}
                      onChange={(e) => onToggleOne?.(row, e.target.checked)}
                      inputProps={{
                        "aria-label": isSelectable
                          ? `Select ${row.employee?.name || "employee"}`
                          : "Row disabled because status does not match the current selection",
                      }}
                      sx={SELECT_CHECKBOX_SX}
                    />
                    <span>{row.employee?.name || "—"}</span>
                  </label>
                </td>
                <td>{row.company?.name || "—"}</td>
                <td>{formatDisplayDate(row.completedOn)}</td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};
